import { useState } from 'react';
import {
    Button,
    ButtonVariant,
    Card,
    Chip,
    ChipType,
    CLAIM_SUMMARY_AUDIENCES,
    DEFAULT_NOTE_AUDIENCE,
    Textarea,
} from '@barkibu/noma-commons';
import { useModuleTranslation } from '../../../../utils/useModuleTranslation';
import './claim-summary-card.modules.scss';

export type ClaimSummaryEntry = {
    audience: string;
    description: string;
    updatedAt?: Date;
};

export type ClaimSummaryCardProps = {
    summaries?: ClaimSummaryEntry[];
    readOnly?: boolean;
    onSave?: (content: string, audience: string) => Promise<void>;
};

const ClaimSummaryCard = ({ summaries = [], readOnly = false, onSave }: ClaimSummaryCardProps) => {
    const { t } = useModuleTranslation();
    const [selectedAudience, setSelectedAudience] = useState<string>(DEFAULT_NOTE_AUDIENCE);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const current = summaries.find((entry) => entry.audience === selectedAudience);

    const selectAudience = (audience: string) => {
        if (audience === selectedAudience) return;
        setSelectedAudience(audience);
        setEditing(false);
        setError(undefined);
    };

    const startEditing = () => {
        setDraft(current?.description ?? '');
        setError(undefined);
        setEditing(true);
    };

    const cancelEditing = () => {
        setEditing(false);
        setError(undefined);
    };

    const handleSave = async () => {
        if (onSave == null) return;
        setSaving(true);
        setError(undefined);
        try {
            await onSave(draft, selectedAudience);
            setEditing(false);
        } catch {
            setError(t('summary_view.claim_summary.save_error') || undefined);
        } finally {
            setSaving(false);
        }
    };

    const canEdit = !readOnly && onSave != null;

    return (
        <Card title={t('summary_view.claim_summary.title')} className="claim-summary-card">
            <div className="claim-summary-card__audience-selector" role="listbox" aria-label="Summary audience">
                {CLAIM_SUMMARY_AUDIENCES.map((audience) => (
                    <Chip
                        key={audience}
                        type={ChipType.OPTION}
                        text={t(`summary_view.claim_summary.audience.${audience}`)}
                        checked={audience === selectedAudience}
                        onClick={() => selectAudience(audience)}
                    />
                ))}
            </div>
            {editing ? (
                <div className="claim-summary-card__editor">
                    <Textarea
                        value={draft}
                        aria-label={t('summary_view.claim_summary.title') || undefined}
                        placeholder={t('summary_view.claim_summary.placeholder') || undefined}
                        disabled={saving}
                        onChange={setDraft}
                    />
                    {error && <p className="claim-summary-card__error">{error}</p>}
                    <div className="claim-summary-card__actions">
                        <Button
                            variantName={ButtonVariant.TERTIARY}
                            text={t('summary_view.claim_summary.cancel')}
                            onClick={cancelEditing}
                            disabled={saving}
                        />
                        <Button
                            variantName={ButtonVariant.PRIMARY}
                            text={t('summary_view.claim_summary.save')}
                            onClick={handleSave}
                            disabled={saving}
                        />
                    </div>
                </div>
            ) : (
                <div className="claim-summary-card__content">
                    {current?.description ? (
                        <p className="claim-summary-card__text">{current.description}</p>
                    ) : (
                        <p className="claim-summary-card__empty">{t('summary_view.claim_summary.empty')}</p>
                    )}
                    {current?.updatedAt && (
                        <span className="claim-summary-card__last-modified">
                            {current.updatedAt.toLocaleDateString()}
                        </span>
                    )}
                    {canEdit && (
                        <div className="claim-summary-card__actions">
                            <Button
                                variantName={ButtonVariant.SECONDARY}
                                text={t('summary_view.claim_summary.edit')}
                                onClick={startEditing}
                            />
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
};

export default ClaimSummaryCard;
